import axios from 'axios';

// Base URL for the API - configured via .env file
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create a configured axios instance for JSON requests
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Include credentials with requests
});

// Add request interceptor to include user credentials in headers
apiClient.interceptors.request.use(
  config => {
    // Get user data from localStorage
    const userStr = localStorage.getItem('rgmUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.id && user.userName) {
          config.headers['x-user-id'] = user.id;
          config.headers['x-user-name'] = user.userName;
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Handle requests for multipart form data (file uploads)
export const uploadFormData = async (endpoint, formData, method = 'POST', onProgress = null) => {
  try {
    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Set up progress tracking
      if (onProgress && typeof onProgress === 'function') {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }
      
      // Set up response handlers
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (parseError) {
            reject(new Error('Invalid JSON response from server'));
          }
        } else {
          const errorText = xhr.responseText || 'Unknown error';
          let responseData = null;
          try {
            responseData = JSON.parse(errorText);
          } catch (parseError) {
            // Keep the raw response below when the server did not return JSON.
          }
          const error = new Error(responseData?.message || `Server responded with status: ${xhr.status}. ${errorText}`);
          error.status = xhr.status;
          error.responseData = responseData;
          reject(error);
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Network error occurred during upload'));
      });
      
      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout - please try again'));
      });
      
      // Configure request
      xhr.open(method, `${API_BASE_URL}${endpoint}`);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.withCredentials = true;
      xhr.timeout = 30 * 60 * 1000; // 30 minutes timeout
      
      // Add user credentials to headers
      const userStr = localStorage.getItem('rgmUser');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.id && user.userName) {
            xhr.setRequestHeader('x-user-id', user.id);
            xhr.setRequestHeader('x-user-name', user.userName);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
      
      // Send the request
      xhr.send(formData);
    });
  } catch (error) {
    console.error(`Error ${method} to ${endpoint}:`, error);
    throw error;
  }
};

// Add response interceptor to handle errors consistently
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Create a more user-friendly error message
    if (error.response) {
      // The server responded with a status code outside of 2xx
      console.error('Server error:', error.response.data);
      error.message = `Server responded with error ${error.response.status}: ${error.response.data.message || 'Unknown error'}`;
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network error:', error.request);
      error.message = 'Network error: Could not connect to the server. Please check your internet connection.';
    }
    return Promise.reject(error);
  }
);

// Export the base URL for use in components that need direct URL construction
export { API_BASE_URL };

export default apiClient; 
